import { useEffect, useMemo, useRef } from 'react';
import {
  useGetSchoolTuitionDashboardTreeQuery,
  useGetSchoolTuitionFamilyLedgerTreeQuery,
} from '../../../state/domainRouterApi';
import {
  normalizeTuitionDashboardSnapshotFromTree,
  normalizeTuitionFamilyLedgerSnapshotFromTree,
  type TuitionDashboardSnapshot,
  type TuitionFamilyLedgerSnapshot,
} from './tuitionData';

const resolveQueryErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return fallback;
};

export const useSchoolTuitionDashboardObjectTree = (options: {
  academicYear: string;
  limit?: number;
  searchQuery?: string;
  skip?: boolean;
}) => {
  const query = useGetSchoolTuitionDashboardTreeQuery(
    {
      academicYear: options.academicYear,
      limit: options.limit ?? 100,
      offset: 0,
      searchQuery: options.searchQuery,
    },
    {
      skip: options.skip === true,
      refetchOnMountOrArgChange: 60,
    },
  );

  const snapshot = useMemo<TuitionDashboardSnapshot | null>(
    () => (
      query.data
        ? normalizeTuitionDashboardSnapshotFromTree(
            query.data as Record<string, unknown>,
            options.academicYear,
          )
        : null
    ),
    [options.academicYear, query.data],
  );

  const lastResolvedSnapshotRef = useRef<TuitionDashboardSnapshot | null>(null);

  useEffect(() => {
    if (snapshot != null) {
      lastResolvedSnapshotRef.current = snapshot;
    }
  }, [snapshot]);

  const resolvedSnapshot = snapshot ?? lastResolvedSnapshotRef.current;

  return {
    snapshot: resolvedSnapshot,
    isLoading: (query.isLoading || query.isFetching) && resolvedSnapshot == null,
    isFetching: query.isFetching,
    error: query.isError
      ? resolveQueryErrorMessage(query.error, 'Failed to load tuition dashboard.')
      : null,
    refetch: query.refetch,
  };
};

export const useSchoolTuitionFamilyLedgerObjectTree = (options: {
  academicYear: string;
  familyId: string;
  seededSnapshot?: TuitionFamilyLedgerSnapshot | null;
  skip?: boolean;
}) => {
  const hasFamilyId = options.familyId.trim().length > 0;
  const query = useGetSchoolTuitionFamilyLedgerTreeQuery(
    {
      academicYear: options.academicYear,
      familyId: options.familyId,
    },
    {
      skip: options.skip === true || !hasFamilyId,
      refetchOnMountOrArgChange: 60,
    },
  );

  const snapshot = useMemo<TuitionFamilyLedgerSnapshot | null>(
    () => (
      query.data
        ? normalizeTuitionFamilyLedgerSnapshotFromTree(
            query.data as Record<string, unknown>,
            options.academicYear,
          )
        : null
    ),
    [options.academicYear, query.data],
  );

  const lastResolvedSnapshotRef = useRef<TuitionFamilyLedgerSnapshot | null>(
    options.seededSnapshot ?? null,
  );

  useEffect(() => {
    if (options.seededSnapshot != null && lastResolvedSnapshotRef.current == null) {
      lastResolvedSnapshotRef.current = options.seededSnapshot;
    }
  }, [options.seededSnapshot]);

  useEffect(() => {
    if (snapshot != null) {
      lastResolvedSnapshotRef.current = snapshot;
    }
  }, [snapshot]);

  const resolvedSnapshot = snapshot ?? options.seededSnapshot ?? lastResolvedSnapshotRef.current;

  return {
    snapshot: resolvedSnapshot,
    isLoading: hasFamilyId && (query.isLoading || query.isFetching) && resolvedSnapshot == null,
    isFetching: query.isFetching,
    error: query.isError
      ? resolveQueryErrorMessage(query.error, 'Failed to load tuition dashboard.')
      : null,
    refetch: query.refetch,
  };
};
